// CVEditor — per-role tailor pass + PDF export. Both run as persistent jobs.
// Top-right action is "Tailor All": fires a tailor_cv job for every selected
// match so the user can kick off the whole batch in one click. Export PDF
// moves to a per-CV action that only enables after tailor completes.

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, ExternalLink, X } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import LogoSquare from '../components/ui/LogoSquare';
import Progress from '../components/ui/Progress';
import { useJobRunner } from '../lib/useJobRunner';
import { apiDelete, apiGet, apiPost } from '../lib/api';
import { useZh } from '../lib/useProduct';
import type { Match } from '../lib/types';

interface TailoredItem {
  pdf_file: string;
  pdf_url: string;
  candidate_slug: string | null;
  company_slug: string | null;
  company: string;
  date: string;
  bytes: number;
  mtime: number;
}
interface TailoredResponse { total: number; items: TailoredItem[] }

function companySlugify(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

const JOB_KEY_PREFIX = 'nice2job.job.';

export default function CVEditor() {
  const nav = useNavigate();
  const zh = useZh();
  const [selected, setSelected] = useState<Match[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  // Bumped by Tailor All after it writes new job_ids to localStorage, so the
  // currently-mounted pane remounts and its useJobRunner re-reads the key.
  const [remountNonce, setRemountNonce] = useState(0);

  useEffect(() => {
    const raw = sessionStorage.getItem('nice2job.cv.selected');
    if (raw) {
      try {
        setSelected(JSON.parse(raw) as Match[]);
      } catch {
        setSelected([]);
      }
    }
  }, []);

  if (selected.length === 0) {
    return (
      <div className="page-enter">
        <PageHeader eyebrow={zh ? '简历定制' : 'CV writer'} title={zh ? '先选一个职位。' : 'Pick a role first.'} sub={zh ? '回到列表页，至少选一个匹配的职位。' : 'Come back via the picker and select at least one match.'} />
        <button className="btn accent" onClick={() => nav('/cv')}>{zh ? '← 去选职位' : '← Go to picker'}</button>
      </div>
    );
  }

  const job = selected[activeIdx];

  return (
    <div className="page-enter">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <button className="btn ghost" onClick={() => nav(-1)}>{zh ? '← 返回选择' : '← Back to picker'}</button>
        <div style={{ textAlign: 'center' }}>
          <div className="tab">{zh ? '正在定制' : 'Editing'}</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{zh ? `${selected.length} 份定制简历` : `${selected.length} tailored CV${selected.length > 1 ? 's' : ''}`}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <CancelAllButton jobs={selected} onCancelled={() => setRemountNonce((n) => n + 1)} />
          <TailorAllButton jobs={selected} onStarted={() => setRemountNonce((n) => n + 1)} />
        </div>
      </div>

      {selected.length > 1 && (
        <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid var(--line)', marginBottom: 18, overflowX: 'auto' }}>
          {selected.map((j, i) => (
            <div
              key={j.id}
              onClick={() => setActiveIdx(i)}
              style={{
                padding: '10px 16px',
                borderBottom: i === activeIdx ? '2px solid var(--pri)' : '2px solid transparent',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap',
              }}
            >
              <LogoSquare name={j.company} size={22} />
              <div>
                <div style={{ fontSize: 12.5, fontWeight: i === activeIdx ? 600 : 500, color: i === activeIdx ? 'var(--ink)' : 'var(--ink2)' }}>{j.company}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <JobTailorPane key={`${job.id}:${remountNonce}`} job={job} />
    </div>
  );
}

function JobTailorPane({ job }: { job: Match }) {
  const zh = useZh();
  // One persistent job per (company) so switching tabs keeps each role's run visible.
  // Reattach: if another device kicked off tailor_cv for this URL, adopt it
  // so this tab streams the same log instead of letting the user re-click.
  const runner = useJobRunner(`cv-tailor-${job.id}`, {
    reattach: {
      cmd: 'tailor_cv',
      match: (p) => p.url === job.url,
    },
  });

  const canRun = !!job.company && !!job.role;

  const start = () =>
    runner.startJob('tailor_cv', { company: job.company, role: job.role, url: job.url });

  const tailorDone = runner.status === 'done';

  // Look up the tailored PDF this job produced. tailor_cv already generates
  // reports/cv-{candidate}-{company}-{date}.pdf — no separate "Export PDF" step.
  const coSlug = companySlugify(job.company);
  const tailoredQ = useQuery({
    queryKey: ['cv-tailored'],
    queryFn: () => apiGet<TailoredResponse>('/api/cv/tailored'),
    staleTime: 5_000,
    refetchInterval: runner.isRunning ? 3000 : false,
  });
  const latestPdf = useMemo(() => {
    const items = tailoredQ.data?.items ?? [];
    const mine = items.filter((it) => it.company_slug === coSlug);
    if (mine.length === 0) return null;
    return mine.sort((a, b) => b.mtime - a.mtime)[0];
  }, [tailoredQ.data, coSlug]);

  // Fallback: if this session has no active jobId but a tailored PDF already
  // exists, fetch the historical log from the server so we can still render
  // the ATS score card. Only fires when we have NO live runner + a PDF.
  const historicalQ = useQuery({
    queryKey: ['tailor-log', coSlug],
    queryFn: () => apiGet<{ log: string; job_id: string; status: string }>(
      '/api/cv/tailor-log',
      { company_slug: coSlug }
    ),
    enabled: !runner.isActive && !!latestPdf && !!coSlug,
    retry: 0,
    staleTime: 60_000,
  });

  // Prefer the live runner's log. If no live session, fall back to the
  // historical log we just fetched. Either way, the same parser renders it.
  const effectiveLog = runner.log || historicalQ.data?.log || '';
  const summary = useMemo(() => summarizeTailorLog(effectiveLog), [effectiveLog]);
  const showSummary = tailorDone || (!!latestPdf && !!historicalQ.data?.log);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 20, alignItems: 'start' }}>
      <div>
        <div className="card" style={{ padding: 22, marginBottom: 14 }}>
          <div className="tab" style={{ marginBottom: 8 }}>{zh ? '目标职位' : 'Target'}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <LogoSquare name={job.company} size={36} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{job.company}</div>
              <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{job.role}</div>
            </div>
          </div>
        </div>
        <div className="card" style={{ padding: 22 }}>
          <div className="tab" style={{ marginBottom: 10 }}>{zh ? '定制状态' : 'Tailor status'}</div>
          <div style={{ fontSize: 13, color: 'var(--ink2)', marginBottom: 14 }}>
            {zh
              ? '以后台任务的方式，用 Ollama 根据你的简历 + 目标职位描述定制。结束时自动生成 PDF——无需单独导出。你可以离开此页稍后再回来。'
              : 'Runs Ollama against your cv.md + the target JD as a background job. The PDF is generated automatically at the end — no separate export step. You can leave this page and come back.'}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              className="btn accent lg"
              disabled={!canRun || runner.isRunning}
              onClick={start}
            >
              {runner.isRunning ? (zh ? '运行中…' : 'Running…') : tailorDone || latestPdf ? (zh ? '再跑一次' : 'Run again') : (zh ? '开始定制' : 'Run tailor pass')}
            </button>
            {runner.isRunning && (
              <button
                className="btn lg"
                onClick={runner.cancel}
                style={{ color: '#b43a3a' }}
                title={zh ? '终止 Ollama 进程并丢弃这次运行' : 'Kill the Ollama process and discard this run'}
              >
                {zh ? '取消' : 'Cancel'}
              </button>
            )}
          </div>
          {runner.isActive && (
            <div style={{ marginTop: 16 }}>
              <Progress value={runner.status === 'done' ? 100 : runner.status === 'failed' ? 100 : Math.min(95, (runner.log.length / 30))} />
              <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 6 }}>
                Job {runner.jobId?.slice(0, 8)} · {runner.status} · {runner.log.length.toLocaleString()} chars
              </div>
            </div>
          )}
          {runner.errorLine && (
            <div style={{ marginTop: 14, padding: '10px 12px', background: '#fcf3e6', border: '1px solid #eddbc1', borderRadius: 8, fontSize: 12, color: 'var(--warn)' }}>
              {runner.errorLine}{zh ? ' — 请确认 Ollama 正在 127.0.0.1:11434 运行。' : ' — check Ollama is running at 127.0.0.1:11434.'}
            </div>
          )}
          {latestPdf && (
            <div style={{ marginTop: 16, padding: '12px 14px', background: 'rgba(45,122,74,0.05)', border: '1px solid rgba(45,122,74,0.25)', borderRadius: 8 }}>
              <div className="tab" style={{ marginBottom: 6 }}>{zh ? '定制版 PDF' : 'Tailored PDF'}</div>
              <div style={{ fontSize: 12, color: 'var(--ink2)', marginBottom: 10, wordBreak: 'break-all' }}>
                {latestPdf.pdf_file}
                <span style={{ color: 'var(--ink3)', marginLeft: 8 }}>
                  · {latestPdf.date} · {(latestPdf.bytes / 1024).toFixed(1)} KB
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <a
                  className="btn"
                  href={latestPdf.pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <ExternalLink size={12} /> {zh ? '预览' : 'Preview'}
                </a>
                <a
                  className="btn accent"
                  href={latestPdf.pdf_url}
                  download={latestPdf.pdf_file}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <Download size={12} /> {zh ? '下载 PDF' : 'Download PDF'}
                </a>
                <button className="btn ghost" onClick={runner.dismiss}>{zh ? '清空日志' : 'Clear log'}</button>
                {/* Armed delete: 1st click → red Confirm; 2nd click → DELETE
                    /api/cv/tailored/:filename. After delete, refetch list +
                    clear runner so UI returns to "Waiting for your go" state. */}
                <DeleteTailoredButton pdfFile={latestPdf.pdf_file} onDeleted={runner.dismiss} />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ padding: 22, minHeight: 400 }}>
        <div className="tab" style={{ marginBottom: 10 }}>{zh ? '结果' : 'Result'}</div>
        {runner.isActive && !tailorDone && (
          <div style={{ fontSize: 13, color: 'var(--ink2)', lineHeight: 1.7 }}>
            {runner.isRunning ? (zh ? '思考中…' : 'Thinking…') : runner.status === 'failed' ? (zh ? '定制失败。' : 'Tailor pass failed.') : ''}
          </div>
        )}
        {!runner.isActive && !latestPdf && (
          <div className="empty" style={{ padding: '32px 12px' }}>
            <span className="serif">{zh ? '等你开始。' : 'Waiting for your go.'}</span>
            {zh ? '点「开始定制」（或上面的「全部定制」）开始。' : 'Click "Run tailor pass" (or "Tailor All" above) to start.'}
          </div>
        )}
        {!runner.isActive && latestPdf && historicalQ.isLoading && (
          <div className="empty" style={{ padding: '32px 12px' }}>
            <span className="serif">{zh ? '加载历史定制报告…' : 'Loading past tailor report…'}</span>
          </div>
        )}
        {!runner.isActive && latestPdf && historicalQ.isError && (
          <div className="empty" style={{ padding: '32px 12px' }}>
            <span className="serif">{zh ? '这份定制简历没有存储日志。' : 'No stored log for this tailored CV.'}</span>
            {zh ? '它是在日志持久化之前生成的。点「再跑一次」重新生成 ATS 报告。' : 'It was generated before logs were persisted. Click "Run again" to regenerate the ATS report.'}
          </div>
        )}
        {showSummary && <TailorSummary summary={summary} />}
      </div>
    </div>
  );
}

// TailorAllButton: fires tailor_cv for every selected match in one click.
// Calls POST /api/jobs directly and writes each job_id to the same
// localStorage key the per-tab useJobRunner reads on mount, so switching
// tabs picks up the running job seamlessly.
function TailorAllButton({ jobs, onStarted }: { jobs: Match[]; onStarted?: () => void }) {
  const zh = useZh();
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(0);

  const startAll = async () => {
    const runnable = jobs.filter((j) => j.company && j.role);
    if (runnable.length === 0) return;
    setBusy(true);
    setStarted(0);
    let ok = 0;
    await Promise.all(runnable.map(async (job) => {
      const payload = JSON.stringify({ company: job.company, role: job.role, url: job.url });
      try {
        const res = await apiPost<{ job_id: string }>('/api/jobs', { cmd: 'tailor_cv', payload });
        try { localStorage.setItem(JOB_KEY_PREFIX + `cv-tailor-${job.id}`, res.job_id); } catch { /* noop */ }
        ok++;
        setStarted(ok);
      } catch { /* individual failures are visible in each per-tab pane */ }
    }));
    setBusy(false);
    if (ok > 0) onStarted?.();
  };

  const label = busy
    ? (zh ? `启动中 ${started}/${jobs.length}…` : `Starting ${started}/${jobs.length}…`)
    : (zh ? `全部定制 (${jobs.length})` : `Tailor all (${jobs.length})`);

  return (
    <button className="btn accent" disabled={busy || jobs.length === 0} onClick={startAll}>
      {label}
    </button>
  );
}

// CancelAllButton: kill every running tailor_cv job in one click.
// Scans localStorage for cv-tailor-{jobId} entries across the selected matches,
// DELETEs each /api/jobs/{id} (SIGTERMs the child process), clears the keys,
// then bumps the parent's remount nonce so the pane's useJobRunner resets.
// Armed two-click pattern keeps it consistent with the rest of the app.
function CancelAllButton({ jobs, onCancelled }: { jobs: Match[]; onCancelled?: () => void }) {
  const zh = useZh();
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cancelled, setCancelled] = useState(0);
  // Tick re-reads localStorage every 2s so the button's running count reflects
  // jobs finishing naturally (useJobRunner clears its key on terminal states
  // elsewhere, but this component doesn't hold a reference to those hooks).
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(t);
  }, [armed]);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 2000);
    return () => clearInterval(id);
  }, []);

  // Gather every tailor job currently tracked in localStorage for these matches.
  const runningIds = jobs
    .map((j) => {
      const key = JOB_KEY_PREFIX + `cv-tailor-${j.id}`;
      try { return { key, jobId: localStorage.getItem(key) }; } catch { return { key, jobId: null }; }
    })
    .filter((x): x is { key: string; jobId: string } => !!x.jobId);

  const nRunning = runningIds.length;

  const handleClick = async () => {
    if (nRunning === 0 || busy) return;
    if (!armed) {
      setArmed(true);
      return;
    }
    setArmed(false);
    setBusy(true);
    setCancelled(0);
    let done = 0;
    await Promise.all(
      runningIds.map(async ({ key, jobId }) => {
        try {
          await fetch(`/api/jobs/${jobId}`, { method: 'DELETE' });
        } catch { /* noop — we clear localStorage regardless so UI unsticks */ }
        try { localStorage.removeItem(key); } catch { /* noop */ }
        done++;
        setCancelled(done);
      })
    );
    setBusy(false);
    onCancelled?.();
  };

  if (nRunning === 0) return null;

  const label = busy
    ? (zh ? `取消中 ${cancelled}/${nRunning}…` : `Cancelling ${cancelled}/${nRunning}…`)
    : armed
      ? (zh ? `再点一次终止 ${nRunning} 个` : `Click again to kill ${nRunning}`)
      : (zh ? `全部取消 (${nRunning})` : `Cancel all (${nRunning})`);

  return (
    <button
      className="btn"
      disabled={busy}
      onClick={handleClick}
      title={armed ? (zh ? '再点一次终止所有正在运行的定制任务' : 'Click again to SIGTERM every running tailor job') : (zh ? '终止所有正在运行的 Ollama 定制进程' : 'Kill all running Ollama tailor processes')}
      style={{
        color: armed ? '#fff' : '#b43a3a',
        background: armed ? '#b43a3a' : 'transparent',
        border: armed ? '1px solid #b43a3a' : undefined,
        transition: 'all 120ms',
      }}
    >
      {label}
    </button>
  );
}

interface TailorLogSummary {
  scoreBefore: number | null;      // 0-100 (ATS Overall %)
  scoreAfter: number | null;       // 0-100
  keywordBefore: number | null;
  keywordAfter: number | null;
  semanticBefore: number | null;
  semanticAfter: number | null;
  missing: string[];               // JD keywords not present in CV after tailoring
  present: string[];
  ragUsed: boolean;
}

// Parses tailor-cv-backend stdout for ATS score blocks + missing keywords.
// The backend emits boxed ATS summaries both BEFORE and AFTER tailoring (and
// optionally a RAG pass). We take the first "Overall" as before and the last
// as after; "Missing" from the LAST block is what the CV still lacks.
function summarizeTailorLog(log: string): TailorLogSummary {
  const empty: TailorLogSummary = {
    scoreBefore: null, scoreAfter: null,
    keywordBefore: null, keywordAfter: null,
    semanticBefore: null, semanticAfter: null,
    missing: [], present: [], ragUsed: false,
  };
  if (!log) return empty;

  // Strip ANSI color codes so regexes match cleanly
  const clean = log.replace(/\x1b\[[0-9;]*m/g, '');

  const overallAll = [...clean.matchAll(/Overall\s+[^\d]*?(\d{1,3})\s*%/g)].map((m) => parseInt(m[1], 10));
  const keywordAll = [...clean.matchAll(/Keyword\s+[^\d]*?(\d{1,3})\s*%/g)].map((m) => parseInt(m[1], 10));
  const semanticAll = [...clean.matchAll(/Semantic\s+[^\d]*?(\d{1,3})\s*%/g)].map((m) => parseInt(m[1], 10));
  const missingAll = [...clean.matchAll(/Missing:\s*(.+?)(?:\n|$)/g)].map((m) => m[1].trim());
  const presentAll = [...clean.matchAll(/Present:\s*(.+?)(?:\n|$)/g)].map((m) => m[1].trim());

  const pickFirstLast = <T,>(arr: T[]): [T | null, T | null] =>
    arr.length === 0 ? [null, null] : arr.length === 1 ? [arr[0], arr[0]] : [arr[0], arr[arr.length - 1]];

  const [o1, o2] = pickFirstLast(overallAll);
  const [k1, k2] = pickFirstLast(keywordAll);
  const [s1, s2] = pickFirstLast(semanticAll);

  // Last Missing/Present lines reflect the final CV after any RAG pass
  const splitList = (csv: string) =>
    csv.split(/[,，;；]/).map((s) => s.trim()).filter(Boolean).filter((s) => !/^\.{3,}$/.test(s));

  // The human-readable "Missing:" line joins keywords with ", " — but the
  // extracted keywords are whole requirements that frequently contain commas of
  // their own ("data visualization tools (Tableau, Power BI)"), so splitting it
  // shreds them into fragments. The backend also emits the same list as JSON in
  // a >>ATS_GAP marker, which is unambiguous; prefer that and keep the split as
  // a fallback for logs written before the marker existed, and for the case
  // where the score cleared 70 and no marker was emitted.
  const gapMatches = [...clean.matchAll(/>>ATS_GAP:(\{.*\})/g)];
  let missing: string[] = [];
  if (gapMatches.length) {
    try {
      const parsed = JSON.parse(gapMatches[gapMatches.length - 1][1]);
      if (Array.isArray(parsed?.missing)) missing = parsed.missing.map(String).filter(Boolean);
    } catch { /* malformed marker → fall through to the text line */ }
  }
  if (!missing.length && missingAll.length) missing = splitList(missingAll[missingAll.length - 1]);

  const present = presentAll.length ? splitList(presentAll[presentAll.length - 1]) : [];

  return {
    scoreBefore: o1, scoreAfter: o2,
    keywordBefore: k1, keywordAfter: k2,
    semanticBefore: s1, semanticAfter: s2,
    missing: missing.slice(0, 12),
    present: present.slice(0, 12),
    ragUsed: /RAG\s*(pass|second)/i.test(clean),
  };
}

function TailorSummary({ summary }: { summary: TailorLogSummary }) {
  const zh = useZh();
  const hasScore = summary.scoreBefore != null && summary.scoreAfter != null;
  const delta = hasScore ? (summary.scoreAfter! - summary.scoreBefore!) : 0;
  const deltaStr = delta > 0 ? `+${delta}` : String(delta);
  const deltaColor = delta > 0 ? '#2d7a4a' : delta < 0 ? '#b43a3a' : 'var(--ink3)';

  return (
    <div style={{ fontSize: 14, lineHeight: 1.7 }}>
      {hasScore ? (
        <div style={{ marginBottom: 20 }}>
          <div className="tab" style={{ marginBottom: 8 }}>{zh ? 'ATS 分数' : 'ATS score'}</div>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 14,
              fontFamily: 'Fraunces, var(--cjk-serif), serif',
              fontSize: 30,
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            <span style={{ color: 'var(--ink3)' }}>{summary.scoreBefore}%</span>
            <span style={{ fontSize: 18, color: 'var(--ink3)' }}>→</span>
            <span style={{ color: 'var(--ink)' }}>{summary.scoreAfter}%</span>
            <span style={{ fontSize: 15, color: deltaColor, fontFamily: 'Inter, var(--cjk-sans), sans-serif' }}>
              {deltaStr} {zh ? '分' : 'pts'}{summary.ragUsed ? (zh ? ' · 已用 RAG' : ' · RAG applied') : ''}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 22, marginTop: 10, fontSize: 11, color: 'var(--ink3)' }}>
            {summary.keywordBefore != null && (
              <div>
                <span className="tab">{zh ? '关键词' : 'Keyword'}</span>{' '}
                <span style={{ color: 'var(--ink2)' }}>
                  {summary.keywordBefore}% → {summary.keywordAfter}%
                </span>
              </div>
            )}
            {summary.semanticBefore != null && (
              <div>
                <span className="tab">{zh ? '语义' : 'Semantic'}</span>{' '}
                <span style={{ color: 'var(--ink2)' }}>
                  {summary.semanticBefore}% → {summary.semanticAfter}%
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ color: 'var(--ink3)', fontSize: 13, marginBottom: 18 }}>
          {zh ? '定制完成。（输出中未检测到 ATS 分数。）' : 'Tailor pass complete. (No ATS score detected in output.)'}
        </div>
      )}

      {summary.missing.length > 0 && (
        <>
          <div className="tab" style={{ marginBottom: 8 }}>
            {zh ? '改进建议 · 简历中仍缺少' : 'Improvement suggestions · still missing in CV'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 8 }}>
            {zh
              ? '这些职位描述里的关键词，定制没能融进去——通常说明你的简历里没有相关证据。要么加一句话证明这项技能，要么把它当作暂时够不着的目标放弃。'
              : "These JD keywords the tailor pass didn't manage to weave in — usually means your CV has no evidence for them. Either add a line that demonstrates the skill, or drop it as a stretch target."}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
            {summary.missing.map((w, i) => (
              <span
                key={i}
                style={{
                  fontSize: 11.5,
                  padding: '3px 9px',
                  borderRadius: 12,
                  background: 'rgba(180,58,58,0.08)',
                  color: '#b43a3a',
                  border: '1px solid rgba(180,58,58,0.2)',
                }}
              >
                {w}
              </span>
            ))}
          </div>
        </>
      )}
      {summary.present.length > 0 && (
        <>
          <div className="tab" style={{ marginBottom: 8 }}>{zh ? '现已包含的职位关键词' : 'JD keywords now present'}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {summary.present.map((w, i) => (
              <span
                key={i}
                style={{
                  fontSize: 11.5,
                  padding: '3px 9px',
                  borderRadius: 12,
                  background: 'rgba(45,122,74,0.08)',
                  color: '#2d7a4a',
                  border: '1px solid rgba(45,122,74,0.25)',
                }}
              >
                {w}
              </span>
            ))}
          </div>
        </>
      )}
      {summary.missing.length === 0 && summary.present.length > 0 && (
        <div style={{ fontSize: 13, color: 'var(--ink3)', marginTop: 10 }}>
          {zh ? '所有追踪的职位关键词都已包含。🎉' : 'All tracked JD keywords present. 🎉'}
        </div>
      )}
    </div>
  );
}

// ── DeleteTailoredButton ──────────────────────────────────────────────────
// Two-click armed delete for the tailored PDF currently shown in the editor.
// Hits DELETE /api/cv/tailored/:filename (server.js:2463) and on success
// invalidates the cv-tailored query so other places re-render too. Calls
// onDeleted() afterwards so the parent can clear its runner.dismiss UI.
function DeleteTailoredButton({ pdfFile, onDeleted }: { pdfFile: string; onDeleted: () => void }) {
  const zh = useZh();
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  const click = async () => {
    if (!armed) { setArmed(true); return; }
    setBusy(true);
    setArmed(false);
    try {
      await apiDelete(`/api/cv/tailored/${encodeURIComponent(pdfFile)}`);
      qc.invalidateQueries({ queryKey: ['cv-tailored'] });
      onDeleted();
    } catch (e) {
      alert((zh ? '删除失败：' : 'Could not delete: ') + (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      className="btn ghost"
      onClick={click}
      disabled={busy}
      title={armed ? (zh ? '再点一次永久删除这个 PDF' : 'Click again to permanently delete this PDF') : (zh ? '删除这份定制 PDF' : 'Delete this tailored PDF')}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        color: armed ? '#b43a3a' : 'var(--ink3)',
        border: armed ? '1px solid #b43a3a' : undefined,
        background: armed ? 'rgba(180,58,58,0.06)' : undefined,
      }}
    >
      <X size={12} /> {busy ? '…' : armed ? (zh ? '确认删除' : 'Confirm delete') : (zh ? '删除 PDF' : 'Delete PDF')}
    </button>
  );
}
